import axios from 'axios';
import { SHARING_AGREEMENT_STATUS, TRUSTCENTER_ENDPOINT } from 'constants/Constants';
import {
    normalizeDeliverToOtherParties,
    normalizeMessageTypesForPut
} from 'components/TrustCenter/store/utils/agreementV2Normalization';
import { getUpdatedByEmailFromState } from 'components/TrustCenter/store/actions/agreementV2Action';
import store from 'store/configure';
import { normalizeSharingRequests, sortSharingRequests } from './sharingRequestModel';

const V1_PAGE_SIZE = 100;
const V2_PAGE_SIZE = 25;
const MAX_PAGES = 50;

const SHARING_AGREEMENT_DTO_KEYS = [
    'id',
    'customerAccount',
    'customerAccountName',
    'createdAt',
    'providerAccount',
    'providerAccountName',
    'direction',
    'validFrom',
    'validTo',
    'deliverToOtherParties',
    'messageTypes'
];

function authHeaders() {
    const headers = {
        Authorization: `Bearer ${sessionStorage.getItem('access_token')}`
    };
    try {
        const updatedByEmail = getUpdatedByEmailFromState(store.getState);
        if (updatedByEmail) {
            headers.UpdatedByEmail = updatedByEmail;
        }
    } catch (error) {
        // Email is optional for the update; keep Authorization-only headers.
    }
    return headers;
}

function v1ConnectionsUrl(connectionId) {
    const base = `https://${TRUSTCENTER_ENDPOINT}/api/v1/trustcenter/provider/connections`;
    return connectionId ? `${base}/${connectionId}` : base;
}

async function fetchV1ByStatus(status) {
    const items = [];
    let page = 1;
    let totalItems = Infinity;

    while (items.length < totalItems && page <= MAX_PAGES) {
        const response = await axios.get(v1ConnectionsUrl(), {
            headers: authHeaders(),
            params: {
                size: V1_PAGE_SIZE,
                page,
                filter: `requestStatus=="${status}"`
            }
        });
        const pageItems = Array.isArray(response?.data?.items) ? response.data.items : [];
        const reportedTotal = Number(response?.data?.totalItems);
        totalItems = Number.isFinite(reportedTotal)
            ? reportedTotal
            : items.length + pageItems.length;
        items.push(...pageItems);
        if (pageItems.length === 0) {
            break;
        }
        page += 1;
    }

    return items;
}

async function fetchV2Agreements(accountId) {
    const url = `https://${TRUSTCENTER_ENDPOINT}/data-sharing/v2/sharing-agreements`;
    const fetchPage = (pageIndex) =>
        axios.get(url, {
            headers: authHeaders(),
            params: {
                accountId,
                pageSize: V2_PAGE_SIZE,
                pageIndex
            }
        });

    const firstResponse = await fetchPage(0);
    const firstItems = Array.isArray(firstResponse?.data?.items) ? firstResponse.data.items : [];
    const totalItems = Number(firstResponse?.data?.totalItems ?? firstItems.length);
    let items = [...firstItems];
    let pageIndex = 1;

    while (items.length < totalItems && pageIndex < MAX_PAGES) {
        const response = await fetchPage(pageIndex);
        const pageItems = Array.isArray(response?.data?.items) ? response.data.items : [];
        if (pageItems.length === 0) {
            break;
        }
        items = items.concat(pageItems);
        pageIndex += 1;
    }

    return items;
}

export async function fetchVisibilitySharing({ accountId } = {}) {
    const [pending, accepted, rejected, v2] = await Promise.allSettled([
        fetchV1ByStatus(SHARING_AGREEMENT_STATUS.PENDING),
        fetchV1ByStatus(SHARING_AGREEMENT_STATUS.ACCEPTED),
        fetchV1ByStatus(SHARING_AGREEMENT_STATUS.REJECTED),
        fetchV2Agreements(accountId)
    ]);

    const v1Results = [pending, accepted, rejected];
    const v1Connections = v1Results.flatMap((result) =>
        result.status === 'fulfilled' ? result.value : []
    );
    const v2Agreements = v2.status === 'fulfilled' ? v2.value : [];
    const errors = [];
    if (v1Results.some((result) => result.status === 'rejected')) {
        errors.push({ message: 'Some sharing agreements could not be loaded.' });
    }
    if (v2.status === 'rejected') {
        errors.push({ message: 'Some sharing agreements could not be loaded.' });
    }

    return {
        rows: sortSharingRequests(
            normalizeSharingRequests({
                v1Connections,
                v2Agreements
            })
        ),
        v2Agreements,
        errors
    };
}

export function decideV1Connection(connectionId, status) {
    return axios.patch(v1ConnectionsUrl(connectionId), { status }, { headers: authHeaders() });
}

export function decideV1Group(connectionId, groupName, status) {
    return axios.patch(
        `${v1ConnectionsUrl(connectionId)}/agreements`,
        { status, groupName },
        { headers: authHeaders() }
    );
}

export function fetchV1Connection(connectionId) {
    return axios
        .get(v1ConnectionsUrl(connectionId), { headers: authHeaders() })
        .then((response) => response.data);
}

export function fetchV1Groups(connectionId) {
    return axios
        .get(
            `https://${TRUSTCENTER_ENDPOINT}/api/v1/trustcenter/connections/${connectionId}/agreements`,
            { headers: authHeaders() }
        )
        .then((response) => response.data || {});
}

export function unwrapSharingAgreement(data) {
    if (data == null || typeof data !== 'object') {
        return data;
    }
    if (data.sharingAgreementDto != null && typeof data.sharingAgreementDto === 'object') {
        return data.sharingAgreementDto;
    }
    return data;
}

export function fetchV2Agreement(agreementId) {
    return axios
        .get(`https://${TRUSTCENTER_ENDPOINT}/data-sharing/v2/sharing-agreements/${agreementId}`, {
            headers: authHeaders()
        })
        .then((response) => unwrapSharingAgreement(response.data));
}

export function buildV2PutBody(agreement) {
    const dto = {};
    SHARING_AGREEMENT_DTO_KEYS.forEach((key) => {
        if (key === 'messageTypes') {
            dto.messageTypes = normalizeMessageTypesForPut(agreement.messageTypes);
            return;
        }
        if (key === 'deliverToOtherParties') {
            dto.deliverToOtherParties = normalizeDeliverToOtherParties(
                agreement.deliverToOtherParties
            );
            return;
        }
        if (agreement[key] !== undefined) {
            dto[key] = agreement[key];
        }
    });
    return dto;
}

export function saveV2Agreement(agreement) {
    return axios.put(
        `https://${TRUSTCENTER_ENDPOINT}/data-sharing/v2/sharing-agreements/${agreement.id}`,
        buildV2PutBody(agreement),
        { headers: authHeaders() }
    );
}
